/**
 * Role-based access control utility for Talaera AI Playground
 * 
 * This module manages user permissions and project access controls.
 * Currently supports email-based access restrictions.
 */

type AccessRule = {
  allowedEmails?: string[];
  allowedDomains?: string[];
  description: string;
};

// Project access configuration
export const PROJECT_ACCESS_RULES: Record<string, AccessRule> = {
  // Pipedrive Tasks is restricted to specific users
  'pipedrive-tasks': {
    allowedEmails: ['mel@talaera.com'],
    description: 'Access to Pipedrive task management and CRM integration'
  },
  // Talk to Tally V2 is restricted to specific users
  'talk-to-tally-v2': {
    allowedEmails: ['mel@talaera.com', 'melmacmahone@gmail.com'],
    allowedDomains: ['talaera.com'],
    description: 'Secure realtime AI voice assistant (Talk to Tally V2)'
  },
  // Prompt Studio - same restriction as Tally V2
  'prompt-studio': {
    allowedEmails: ['mel@talaera.com', 'melmacmahone@gmail.com'],
    allowedDomains: ['talaera.com'],
    description: 'Manage and launch Tally prompts for internal testing'
  },
  // Content Management - limited access list
  'content-management': {
    allowedEmails: ['mel@talaera.com', 'paola@talaera.com', 'anita@talaera.com', 'kayla@talaera.com', 'chanellea1980@gmail.com'],
    description: 'Content publishing pipeline management'
  },
  'course-management': {
    allowedEmails: ['mel@talaera.com', 'paola@talaera.com', 'anita@talaera.com', 'kayla@talaera.com', 'chanellea1980@gmail.com'],
    description: 'Course authoring and generation pipeline'
  },
  // Talk to Tally V1 - allow Talaera domain
  'talk-to-tally': {
    allowedDomains: ['talaera.com'],
    description: 'Realtime AI assistant (Talk to Tally V1)'
  },
  // Skills Manager - allow Talaera domain
  'skills-manager': {
    allowedDomains: ['talaera.com'],
    description: 'Skills Database Manager'
  },
  // Grammar Dictionary - allow Talaera domain
  'grammar-dictionary': {
    allowedDomains: ['talaera.com'],
    description: 'Grammar dictionary and analytics'
  }
  // Add more restricted projects here as needed
  // 'sensitive-project': {
  //   allowedEmails: ['admin@talaera.com', 'manager@talaera.com'],
  //   description: 'Access to sensitive project functionality'
  // }
};

export type ProjectId = string;
export type UserEmail = string;

/**
 * Check if a user has access to a specific project
 * @param projectId - The project identifier
 * @param userEmail - The user's email address
 * @returns boolean indicating if access is granted
 */
export const hasProjectAccess = (projectId: ProjectId, userEmail: UserEmail | undefined): boolean => {
  // If the project is not in the restricted list, everyone has access (even without email)
  const accessRule = PROJECT_ACCESS_RULES[projectId as keyof typeof PROJECT_ACCESS_RULES];
  if (!accessRule) {
    console.log(`🔐 [Access Control] Project "${projectId}" has no restrictions - access granted`);
    return true;
  }

  // For restricted projects, we need a valid user email
  if (!userEmail) {
    console.warn(`🔐 [Access Control] No user email provided for restricted project: ${projectId}`);
    return false;
  }

  // Normalize email to lowercase for comparison
  const normalizedEmail = userEmail.toLowerCase().trim();
  const emailDomain = normalizedEmail.split('@')[1] || '';
  
  // Special debugging for Pipedrive access
  if (projectId === 'pipedrive-tasks') {
    console.log(`🔍 [Pipedrive Debug] Raw user email: "${userEmail}"`);
    console.log(`🔍 [Pipedrive Debug] Normalized email: "${normalizedEmail}"`);
    if (Array.isArray(accessRule.allowedEmails)) {
      console.log(`🔍 [Pipedrive Debug] Allowed emails:`, accessRule.allowedEmails);
      console.log(`🔍 [Pipedrive Debug] Email comparison results:`, 
        accessRule.allowedEmails.map((allowed: string) => ({
          allowed: allowed,
          normalized: allowed.toLowerCase(),
          matches: allowed.toLowerCase() === normalizedEmail
        }))
      );
    }
  }
  
  // Check if user email is in the allowed list for this project
  const emailAllowed = Array.isArray((accessRule as any).allowedEmails)
    ? (accessRule as any).allowedEmails.some((allowedEmail: string) => allowedEmail.toLowerCase() === normalizedEmail)
    : false;

  // Check domain allowlist if configured
  const domainAllowed = Array.isArray((accessRule as any).allowedDomains)
    ? (accessRule as any).allowedDomains.some((d: string) => emailDomain === d.toLowerCase())
    : false;

  const hasAccess = emailAllowed || domainAllowed;
  
  console.log(`🔐 [Access Control] Project: ${projectId}, User: ${normalizedEmail}, Access: ${hasAccess ? 'GRANTED' : 'DENIED'}`);
  
  if (!hasAccess) {
    console.log(`🔐 [Access Control] Denied access reason: ${accessRule.description}`);
    if ((accessRule as any).allowedEmails) {
      console.log(`🔐 [Access Control] Allowed emails: ${(accessRule as any).allowedEmails.join(', ')}`);
    }
    if ((accessRule as any).allowedDomains) {
      console.log(`🔐 [Access Control] Allowed domains: ${(accessRule as any).allowedDomains.join(', ')}`);
    }
  }
  
  return hasAccess;
};

/**
 * Get a list of all restricted projects for audit purposes
 * @returns Array of restricted project information
 */
export const getRestrictedProjects = () => {
  return Object.entries(PROJECT_ACCESS_RULES).map(([projectId, rule]) => ({
    projectId,
    allowedEmails: rule.allowedEmails,
    description: rule.description
  }));
};

/**
 * Check if a user has admin-level access (access to all restricted projects)
 * @param userEmail - The user's email address
 * @returns boolean indicating if user has admin access
 */
export const hasAdminAccess = (userEmail: UserEmail | undefined): boolean => {
  if (!userEmail) return false;
  
  // Define admin emails here
  const adminEmails = ['mel@talaera.com']; // Currently only mel has admin access
  
  const isAdmin = adminEmails.includes(userEmail.toLowerCase().trim());
  console.log(`🔐 [Access Control] Admin check for ${userEmail}: ${isAdmin ? 'ADMIN' : 'REGULAR USER'}`);
  
  return isAdmin;
};

/**
 * Get user role based on their access permissions
 * @param userEmail - The user's email address
 * @returns string indicating user role
 */
export const getUserRole = (userEmail: UserEmail | undefined): 'admin' | 'restricted' | 'standard' => {
  if (!userEmail) return 'standard';
  
  if (hasAdminAccess(userEmail)) {
    return 'admin';
  }
  
  // Check if user has access to any restricted projects
  const hasAnyRestrictedAccess = Object.keys(PROJECT_ACCESS_RULES).some(projectId => 
    hasProjectAccess(projectId, userEmail)
  );
  
  return hasAnyRestrictedAccess ? 'restricted' : 'standard';
};

/**
 * Log access attempt for audit purposes
 * @param projectId - The project being accessed
 * @param userEmail - The user attempting access
 * @param granted - Whether access was granted
 * @param reason - Optional reason for access decision
 */
export const logAccessAttempt = (
  projectId: ProjectId, 
  userEmail: UserEmail | undefined, 
  granted: boolean, 
  reason?: string
) => {
  const timestamp = new Date().toISOString();
  const logLevel = granted ? 'INFO' : 'WARN';
  const status = granted ? 'GRANTED' : 'DENIED';
  
  console.log(`🔐 [${logLevel}] [${timestamp}] Access ${status}: Project="${projectId}", User="${userEmail || 'UNKNOWN'}"${reason ? `, Reason="${reason}"` : ''}`);
  
  // In a production environment, you might want to send this to a logging service
  // or store it in a database for audit trails
}; 